<template>
  <StaffLayout section="Dashboard">
    <h1 class="py-6 text-3xl">AK Preparedness Staff Page</h1>

    <!-- Top Portals Metrics -->
    <div class="mb-6">
      <div class="flex items-center gap-4 px-4 py-2">
        <h2 class="text-xl font-bold">Top {{ selectedTopCount }} Portals</h2>
        <select
          v-model="selectedTopCount"
          class="shadow border rounded py-1 px-2 text-sm text-gray-700 focus:outline-none focus:shadow-outline"
        >
          <option v-for="n in topCountOptions" :key="n" :value="n">Top {{ n }}</option>
        </select>
        <select
          v-model="selectedTimeRange"
          class="shadow border rounded py-1 px-2 text-sm text-gray-700 focus:outline-none focus:shadow-outline"
        >
          <option
            v-for="option in timeRangeOptions"
            :key="option.label"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </div>
      <Loading v-if="isMetricsLoading" />
      <div v-else-if="topPortals" class="grid grid-cols-1 lg:grid-cols-3 gap-4 px-4">
        <div v-for="metric in metricColumns" :key="metric.key" class="border rounded p-4">
          <h3 class="font-bold text-lg mb-2">{{ metric.label }}</h3>
          <div
            v-for="(item, i) in metric.data"
            :key="metric.key + '-' + i"
            @click="metricItemClick(item.siteCode!, item.portalId!, metric.route, metric.query, metric.allPortalsRoute)"
            class="flex justify-between px-2 py-1 my-1 bg-gray-100 border-t cursor-pointer hover:bg-blue-200"
          >
            <span class="text-sm">
              {{ item.portalName }}
              <!-- <span class="text-gray-500">/{{ item.siteCode }}/</span> -->
            </span>
            <span class="font-mono text-sm font-bold">{{ item.count }}</span>
          </div>
          <div v-if="!metric.data?.length" class="text-sm text-gray-500">None.</div>
        </div>
      </div>
    </div>

    <div class="lg:flex flex-row sm:block">
      <div class="basis-1/2 mx-4">
        <h1 class="py-6 text-2xl">
          {{
            portalSearchString.length == 0 ? "10 Most Recent Portals" : "Portal Search"
          }}
        </h1>
        <div class="flex mt-2">
          <input
            v-model.lazy="portalSearchString"
            placeholder="Search Portals"
            class="basis-3/4 shadow appearance-none border rounded py-1 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          />
          <SearchButton :v-on="portalSearchString" class="basis-1/4">
            Search
          </SearchButton>
        </div>
        <Loading v-if="isPortalsLoading" />
        <div v-else>
          <div class="mb-10">
            <div
              v-for="(portal, i) in portalSearchString.length > 0
                ? filteredPortals
                : portalsList"
              :key="i"
              @click="viewPortalClick(portal.siteCode, portal.portalId)"
              class="px-4 py-2 my-2 border-t cursor-pointer hover:bg-blue-200"
              :class="portal.isActive ? 'bg-gray-100' : 'bg-gray-300'"
            >
              <div class="flex flex-row">
                <div class="basis-1/2">
                  <p class="block text-xl">{{ portal.name }}</p>
                  <p>/{{ portal.siteCode }}/</p>
                </div>
                <div class="basis-1/2">
                  <p class="text-right align-baseline h-full">
                    {{ portal.clientName }} > {{ portal.category }}
                  </p>
                </div>
              </div>
              <div>
                <span v-if="!portal.isActive" class="text-sm">(disabled)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="basis-1/2 px-4">
        <h1 class="py-6 text-2xl">
          {{
            personSearchString.length > 0 || recentlyUsedPortals.length == 0
              ? "User Search"
              : "10 Most Recent Users"
          }}
        </h1>
        <div class="flex mt-2">
          <input
            v-model.lazy="personSearchString"
            placeholder="Search Users"
            class="basis-3/4 shadow appearance-none border rounded py-1 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          />
          <SearchButton :v-on="searchPeople" class="basis-1/4"> Search </SearchButton>
        </div>
        <Loading v-if="isPeopleLoading" />
        <div v-else>
          <div v-if="people && people.length > 0">
            <div
              v-for="person in personSearchString.length > 0 ||
              recentlyUsedPortals.length == 0
                ? people
                : peopleList"
              :key="person.personId"
              @click="viewPersonClick(person)"
              class="px-4 py-2 my-2 bg-gray-100 border-t cursor-pointer hover:bg-blue-200"
              :class="person.isActive ? 'bg-gray-100' : 'bg-gray-300'"
            >
              <div>
                {{ person.firstName }} {{ person.lastName }}
                <span class="ml-2 text-sm text-gray-700">{{ person.loginEmail }}</span
                ><br />
                <span class="text-sm text-gray-700">/{{ person.siteCode }}/</span>
                <span v-if="!person.isActive" class="text-sm text-red-500">
                  (Inactive)
                </span>
              </div>
            </div>
          </div>
          <div v-else>
            <span>-- No people found --</span>
          </div>
          <div
            v-if="
              personSearchString.length == 0 &&
              recentlyUsedPortals.length > 0 &&
              recentlyUsedUsers.length > 0
            "
            class="mt-2 text-right"
          >
            <button
              type="button"
              class="text-sm text-blue-500 underline hover:text-blue-700"
              @click="clearRecentUsers"
            >
              Clear
            </button>
          </div>
        </div>
      </div>
    </div>
  </StaffLayout>
</template>

<script setup lang="ts">
import { useBldgStore } from "../../store/bldgStore";
import { usePersonStore } from "../../store/personStore";
import { useAdminStore } from "../../store/adminStore";
import { useUserStore } from "../../store/userStore";
import { GlobalPeopleSearchDTO } from "../../store/generatedFetchClient";
import {
  addToRecentlyUsedPortals,
  getRecentlyUsedPortals,
  addToRecentlyUsedUsers,
  getRecentlyUsedUsers,
  clearRecentlyUsedUsers,
} from "../../helpers/index";

const bldgStore = useBldgStore();
const personStore = usePersonStore();
const adminStore = useAdminStore();
const router = useRouter();
const userStore = useUserStore();

const isPortalsLoading = ref(false);
const isPeopleLoading = ref(false);

//#region Top Portals Metrics
const timeRangeOptions = [
  { label: "Last 7 days", value: 7 },
  { label: "Last 30 days", value: 30 },
  { label: "Last 90 days", value: 90 },
  { label: "Last 365 days", value: 365 },
  { label: "All time", value: undefined },
];
const topCountOptions = [5, 10, 15, 20];
const selectedTimeRange = ref<number | undefined>(30);
const selectedTopCount = ref(5);
const isMetricsLoading = ref(false);

const topPortals = computed(() => adminStore.topPortals);

const metricsDateRange = computed(() => {
  if (selectedTimeRange.value == null) return {};
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - selectedTimeRange.value);
  return {
    start: start.toISOString().split("T")[0],
    end: end.toISOString().split("T")[0],
  };
});

const metricColumns = computed(() => [
  {
    key: "alertSends",
    label: "Alert Sends",
    data: topPortals.value?.topByAlertSends,
    route: "alert/history",
    allPortalsRoute: true,
    query: metricsDateRange.value,
  },
  {
    key: "alertRecipients",
    label: "Alert Recipients",
    data: topPortals.value?.topByAlertRecipients,
    route: "alert/history",
    allPortalsRoute: true,
    query: metricsDateRange.value,
  },
  {
    key: "logins",
    label: "Logins",
    data: topPortals.value?.topByLogins,
    route: "portal",
  },
  {
    key: "trainingCompletions",
    label: "Training Completions",
    data: topPortals.value?.topByTrainingCompletions,
    route: "reports/trainingreport",
    query: metricsDateRange.value,
  },
  {
    key: "registrations",
    label: "Registrations",
    data: topPortals.value?.topByRegistrations,
    route: "portal",
  },
  {
    key: "accomplishments",
    label: "VR Accomplishments",
    data: topPortals.value?.topByAccomplishments,
    route: "reports/vrreport",
  },
]);

async function loadMetrics() {
  isMetricsLoading.value = true;
  await adminStore.getTopPortals(selectedTimeRange.value, selectedTopCount.value);
  isMetricsLoading.value = false;
}

watch(selectedTimeRange, loadMetrics);
watch(selectedTopCount, loadMetrics);
//#endregion

const isLoggedIn = computed(() => userStore.isLoggedIn);

let portals = computed(() => bldgStore.properties);
let recentlyUsedPortals = ref(getRecentlyUsedPortals());

let portalsList = computed(() => {
  let recentPortalsList = portals.value
    .filter((p) => recentlyUsedPortals.value.includes(p.portalId))
    .sort(
      (a, b) =>
        recentlyUsedPortals.value.indexOf(a.portalId) -
        recentlyUsedPortals.value.indexOf(b.portalId)
    );
  if (recentPortalsList.length >= 10) {
    return recentPortalsList;
  } else {
    return [
      ...recentPortalsList,
      ...portals.value
        .filter((portal) => !recentlyUsedPortals.value.includes(portal.portalId))
        .splice(0, 10 - recentPortalsList.length),
    ];
  }
});

let portalSearchString = ref("");

var filteredPortals = computed(() =>
  portals.value.filter(
    (property) =>
      property.clientName
        .toLowerCase()
        .includes(portalSearchString.value.toLowerCase()) ||
      property.category.toLowerCase().includes(portalSearchString.value.toLowerCase()) ||
      property.name.toLowerCase().includes(portalSearchString.value.toLowerCase()) ||
      property.siteCode.toLowerCase().includes(portalSearchString.value.toLowerCase())
  )
);

onMounted(loadData);
watch(isLoggedIn, loadData);

async function loadData() {
  if (isLoggedIn) {
    isPortalsLoading.value = true;
    bldgStore.getPropertyList().then(() => {
      isPortalsLoading.value = false;
    });
    searchPeople();
    loadMetrics();
    await adminStore.getCounts();
  }
}

let people = ref(null as GlobalPeopleSearchDTO[]);

function metricItemClick(
  siteCode: string,
  portalId: number,
  route: string,
  query?: Record<string, string>,
  allPortalsRoute?: boolean
) {
  addToRecentlyUsedPortals(portalId);
  if (allPortalsRoute) {
    router.push({
      path: "/staff/alert/history/all",
      query: { ...query, portalId: String(portalId) },
    });
  } else {
    router.push({ path: `/staff/${siteCode}/${route}`, query });
  }
}

function viewPortalClick(siteCode: string, portalId: number) {
  addToRecentlyUsedPortals(portalId);
  router.push(`/staff/${siteCode}/portal`);
}

let recentlyUsedUsers = ref(getRecentlyUsedUsers());

function clearRecentUsers() {
  clearRecentlyUsedUsers();
  recentlyUsedUsers.value = [];
}

let peopleList = computed(() => {
  if (recentlyUsedUsers.value.length >= 10) {
    return recentlyUsedUsers.value;
  } else {
    return [
      ...recentlyUsedUsers.value,
      ...people.value
        .filter(
          (person) =>
            !recentlyUsedUsers.value.map((p) => p.personId).includes(person.personId)
        )
        .splice(0, 10 - recentlyUsedUsers.value.length),
    ];
  }
});

let personSearchString = ref("");
watch(personSearchString, searchPeople);

async function searchPeople() {
  isPeopleLoading.value = true;
  people.value = await personStore.globalSearchPeople(personSearchString.value);
  isPeopleLoading.value = false;
}

function viewPersonClick(person: GlobalPeopleSearchDTO) {
  addToRecentlyUsedUsers(person);
  router.push(`/staff/${person.siteCode}/user/${person.personId}`);
}
</script>
